import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AuthRightSection from '../components/AuthRightSection';
import { authApi } from '../services/api';
import { getApiErrorMessage } from '../utils/errors';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Redirect to sign in 3 seconds after a successful reset (cleared on unmount)
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => {
      navigate('/signin');
    }, 3000);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid or missing reset token');
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        await authApi.verifyResetToken(token);
        setTokenValid(true);
      } catch (err: any) {
        console.error('Token verification error:', err);
        setTokenValid(false);
        if (err.response?.status === 400) {
          setError('This reset link has expired or is invalid. Please request a new one.');
        } else {
          setError('Unable to verify reset link. Please try again.');
        }
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate password
    if (!newPassword.trim()) {
      setError('Please enter a new password');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setLoading(true);

    try {
      await authApi.resetPassword(token, newPassword);
      // Redirects to sign in after 3 seconds (see effect above)
      setSuccess(true);
    } catch (err: any) {
      console.error('Password reset error:', err);

      if (err.response?.status === 400) {
        setError('This reset link has expired or is invalid. Please request a new one.');
      } else {
        setError(getApiErrorMessage(err, 'Failed to reset password. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="bg-[#ffffff] relative w-full min-h-screen flex flex-col lg:flex-row"
      data-name="Reset Password"
    >
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-8 sm:gap-12 lg:gap-20 items-center justify-center max-w-[673px] w-full">
          <div className="flex flex-col gap-8 items-start justify-start w-full">
            {verifying ? (
              <div className="flex flex-col gap-4 items-center justify-center w-full text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
                <div className="font-['Ovo',_serif] text-[#767472] text-[16px]">
                  Verifying reset link...
                </div>
              </div>
            ) : !tokenValid ? (
              <div className="flex flex-col gap-6 items-center justify-center w-full text-center">
                {/* Error icon */}
                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>

                <div className="font-['Ovo',_serif] text-[#000000] text-[24px] sm:text-[28px] lg:text-[32px] uppercase">
                  INVALID RESET LINK
                </div>

                <div className="font-['Ovo',_serif] font-normal text-[#767472] text-[16px] max-w-md">
                  {error ||
                    'This password reset link has expired or is invalid. Please request a new one.'}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mt-4">
                  <Link
                    to="/forgot-password"
                    className="border border-[#1f1f1f] border-solid px-6 py-3 font-['Ovo',_serif] text-[#000000] text-[18px] uppercase bg-transparent hover:bg-[#1f1f1f] hover:text-white transition-colors text-center"
                  >
                    REQUEST NEW LINK
                  </Link>
                  <Link
                    to="/signin"
                    className="border border-[#c6a664] border-solid px-6 py-3 font-['Ovo',_serif] text-[#c6a664] text-[18px] uppercase bg-transparent hover:bg-[#c6a664] hover:text-white transition-colors text-center"
                  >
                    BACK TO SIGN IN
                  </Link>
                </div>
              </div>
            ) : success ? (
              <div className="flex flex-col gap-6 items-center justify-center w-full text-center">
                {/* Success icon */}
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>

                <div className="font-['Ovo',_serif] text-[#000000] text-[24px] sm:text-[28px] lg:text-[32px] uppercase">
                  PASSWORD RESET SUCCESSFUL
                </div>

                <div className="font-['Ovo',_serif] font-normal text-[#767472] text-[16px] max-w-md">
                  Your password has been reset successfully. You can now log in with your new
                  password.
                </div>

                <div className="font-['Ovo',_serif] font-normal text-[#c6a664] text-[14px]">
                  Redirecting to sign in page...
                </div>

                <Link
                  to="/signin"
                  className="border border-[#1f1f1f] border-solid px-6 py-3 font-['Ovo',_serif] text-[#000000] text-[18px] uppercase bg-transparent hover:bg-[#1f1f1f] hover:text-white transition-colors text-center"
                >
                  SIGN IN NOW
                </Link>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex flex-col gap-4 items-start justify-center w-full">
                  <div className="font-['Ovo',_serif] text-[#000000] text-[24px] sm:text-[28px] lg:text-[32px] text-center uppercase w-full">
                    CREATE NEW PASSWORD
                  </div>
                  <div className="font-['Ovo',_serif] font-normal text-[#767472] text-[16px] text-center w-full">
                    Enter your new password below.
                  </div>
                </div>

                {/* Error message */}
                {error && (
                  <div
                    className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded w-full"
                    role="alert"
                  >
                    {error}
                  </div>
                )}

                {/* Form */}
                <form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-6 items-center justify-center w-full"
                >
                  <div className="flex flex-col gap-1 w-full">
                    <label
                      htmlFor="newPassword"
                      className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[16px]"
                    >
                      New Password
                    </label>
                    <input
                      id="newPassword"
                      type="password"
                      placeholder="Enter new password (minimum 8 characters)"
                      className="h-14 w-full border border-[#eaeaea] px-6 py-4 font-['Ovo',_serif] text-[16px] text-[#1f1f1f] placeholder:text-[#767472] focus:outline-none focus:border-[#c6a664] focus:ring-1 focus:ring-[#c6a664]"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      disabled={loading}
                      autoFocus
                      minLength={8}
                    />
                  </div>

                  <div className="flex flex-col gap-1 w-full">
                    <label
                      htmlFor="confirmPassword"
                      className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[16px]"
                    >
                      Confirm New Password
                    </label>
                    <input
                      id="confirmPassword"
                      type="password"
                      placeholder="Re-enter new password"
                      className="h-14 w-full border border-[#eaeaea] px-6 py-4 font-['Ovo',_serif] text-[16px] text-[#1f1f1f] placeholder:text-[#767472] focus:outline-none focus:border-[#c6a664] focus:ring-1 focus:ring-[#c6a664]"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                      minLength={8}
                    />
                  </div>

                  <button
                    type="submit"
                    className="border border-[#1f1f1f] border-solid px-6 py-3 font-['Ovo',_serif] text-[#000000] text-[20px] uppercase bg-transparent hover:bg-[#1f1f1f] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                    disabled={loading}
                  >
                    {loading ? 'RESETTING PASSWORD...' : 'RESET PASSWORD'}
                  </button>
                </form>

                {/* Back to Sign In */}
                <div className="flex flex-row gap-1 items-center justify-center w-full">
                  <div className="font-['Ovo',_serif] font-normal text-[#000000] text-[16px]">
                    Remember your password?
                  </div>
                  <Link
                    to="/signin"
                    className="font-['Ovo',_serif] font-semibold text-[#c6a664] text-[16px] underline"
                  >
                    Sign In
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right side - Illustration */}
      <div className="hidden md:block flex-1 min-h-[400px] lg:min-h-screen">
        <AuthRightSection />
      </div>
    </div>
  );
};

export default ResetPassword;
